import React, { useMemo, useCallback, useEffect, useRef, useState, memo } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TableSortLabel } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import CartTableRow from "./CartTableRow";
import CartTableRowSkeleton from "./CartTableRowSkeleton";
import { applyShowPromoLabelDedupe, buildCartPromoGroupBuckets, getCartPromoGroupItemsFromBuckets, getCartItemNormalizedFields } from "../utils/cartCalculations";
import { buildQtyControlMaxBuckets, getQtyControlBucketItems } from "../utils/cartQtyValidation";
import { PREFIX } from "../../../common/Config";
import { useCartTableKeyboardNavigation, isInsideModalDialog, isOfferProductsDialogOpen } from "../utils/cartTableKeyboard";
import { isCartLineGetOrFreeRole } from "../../Promotion/utils/promotionOfferProductsShared";
import "../../../common/commonStyles.css";

const getCartLineCode = (item) =>
  String(item?.code || item?.product_code || "").trim().toLowerCase();

const getCartLineDescription = (item) =>
  String(item?.desc || item?.name || item?.product_name || "").trim().toLowerCase();

const VIRTUALIZE_AFTER_ROWS = 40;
const ROW_ESTIMATE_PX = 56;
const ROW_OVERSCAN = 10;
const EMPTY_BUNDLE_DISCOUNTS = [];
const EMPTY_VISIBLE_RANGE = { start: 0, end: 0, padTop: 0, padBottom: 0 };

const computeCartVisibleRange = (scrollTop, viewportHeight, rowCount, virtualize) => {
  if (!virtualize) {
    return { start: 0, end: rowCount, padTop: 0, padBottom: 0 };
  }
  const start = Math.max(0, Math.floor(scrollTop / ROW_ESTIMATE_PX) - ROW_OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / ROW_ESTIMATE_PX) + ROW_OVERSCAN * 2;
  const end = Math.min(rowCount, start + visibleCount);
  return {
    start,
    end,
    padTop: start * ROW_ESTIMATE_PX,
    padBottom: Math.max(0, (rowCount - end) * ROW_ESTIMATE_PX),
  };
};

const cartRowPrimitive = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "1" : "0";
  return String(value);
};

const hashCartRowVersionParts = (parts) => {
  let hash = 2166136261;
  const token = parts.join("\u0001");
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const cartBucketLinesToken = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return "";
  const parts = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line) continue;
    parts.push(
      cartRowPrimitive(line.id),
      cartRowPrimitive(line.qty ?? line.order_qty ?? line.quantity),
      cartRowPrimitive(line.allot_qty),
      cartRowPrimitive(line.showPromo),
      cartRowPrimitive(line.promoDisplaySets),
      cartRowPrimitive(line.promotion_discount),
      cartRowPrimitive(line.discount)
    );
  }
  return parts.join(",");
};

const matchingBundleToken = (item, bundleDiscounts) => {
  if (!Array.isArray(bundleDiscounts) || bundleDiscounts.length === 0) return "";
  const promoId = String(
    item?.promotion_id ?? item?.promoId ?? item?.promosId ?? item?.promo_id ?? ""
  ).trim();
  if (!promoId) return "";
  for (let i = 0; i < bundleDiscounts.length; i += 1) {
    const bundle = bundleDiscounts[i];
    if (String(bundle?.promotion_id ?? "").trim() !== promoId) continue;
    return [
      cartRowPrimitive(bundle.sets_count),
      cartRowPrimitive(bundle.complete_sets),
      cartRowPrimitive(bundle.get_sets_count),
      cartRowPrimitive(bundle.buy_total_discount),
      cartRowPrimitive(bundle.get_total_discount),
      cartRowPrimitive(bundle.total_promo_discount),
    ].join(":");
  }
  return "";
};

const lineDiscountMapToken = (item, bundleLineDiscountByKey) => {
  if (!bundleLineDiscountByKey || !item) return "";
  const key = String(item.id ?? item.product_id ?? "");
  if (!key) return "";
  if (typeof bundleLineDiscountByKey.get === "function") {
    return cartRowPrimitive(bundleLineDiscountByKey.get(key));
  }
  if (typeof bundleLineDiscountByKey === "object") {
    return cartRowPrimitive(bundleLineDiscountByKey[key]);
  }
  return "";
};

const buildCartTableRowVersion = (
  item,
  qtyControlBucketItems,
  promoGroupItems,
  bundleDiscounts,
  bundleLineDiscountByKey
) =>
  hashCartRowVersionParts([
    cartRowPrimitive(item?.id),
    cartRowPrimitive(item?.product_id ?? item?.productId),
    cartRowPrimitive(item?.qty ?? item?.order_qty ?? item?.quantity),
    cartRowPrimitive(item?.allot_qty),
    cartRowPrimitive(item?.accept_qty),
    cartRowPrimitive(item?.price),
    cartRowPrimitive(item?.unit_price),
    cartRowPrimitive(item?.cart_price),
    cartRowPrimitive(item?.discount),
    cartRowPrimitive(item?.discount_type),
    cartRowPrimitive(item?.item_discount),
    cartRowPrimitive(item?.discount_percentage),
    cartRowPrimitive(item?.promotion_discount),
    cartRowPrimitive(item?.promo_disc_percentage),
    cartRowPrimitive(item?._actual_sub_total),
    cartRowPrimitive(item?.sub_total ?? item?.subtotal),
    cartRowPrimitive(item?.promo_type ?? item?.promoType),
    cartRowPrimitive(item?.promotion_id ?? item?.promoId ?? item?.promosId),
    cartRowPrimitive(item?.showPromo),
    cartRowPrimitive(item?.promoDisplaySets),
    cartRowPrimitive(item?.promotion_name ?? item?.promo_name),
    cartRowPrimitive(item?.generated),
    cartRowPrimitive(item?.is_promoted),
    cartRowPrimitive(item?.promotion_applied),
    cartRowPrimitive(item?.type ?? item?.order_type),
    cartRowPrimitive(item?.unit_order_type),
    cartRowPrimitive(item?.discount_comment ?? item?.comment),
    cartRowPrimitive(item?.code ?? item?.product_code),
    cartRowPrimitive(item?.desc ?? item?.name ?? item?.product_name),
    cartRowPrimitive(item?.tax_code),
    cartRowPrimitive(item?.tax_rate_percent ?? item?.item_tax),
    cartRowPrimitive(item?.box_price),
    matchingBundleToken(item, bundleDiscounts),
    lineDiscountMapToken(item, bundleLineDiscountByKey),
    cartBucketLinesToken(qtyControlBucketItems),
    cartBucketLinesToken(promoGroupItems),
  ]);

const buildCartRevisionToken = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "0";
  const parts = [];
  for (let i = 0; i < rows.length; i += 1) {
    const item = rows[i];
    parts.push(
      cartRowPrimitive(item?.id),
      cartRowPrimitive(item?.product_id ?? item?.productId),
      cartRowPrimitive(item?.qty ?? item?.order_qty ?? item?.quantity),
      cartRowPrimitive(item?.allot_qty),
      cartRowPrimitive(item?.price),
      cartRowPrimitive(item?.unit_price),
      cartRowPrimitive(item?.discount),
      cartRowPrimitive(item?.promotion_discount),
      cartRowPrimitive(item?.promo_type ?? item?.promoType),
      cartRowPrimitive(item?.promotion_id ?? item?.promoId ?? item?.promosId),
      cartRowPrimitive(item?.showPromo),
      cartRowPrimitive(item?.promoDisplaySets),
      cartRowPrimitive(item?.promotion_name ?? item?.promo_name),
      cartRowPrimitive(item?.type ?? item?.order_type)
    );
  }
  return String(hashCartRowVersionParts(parts));
};

const CartTable = ({
  cartItems,
  onTypeChange,
  onAddAsNewType = null,
  onQuantityChange,
  onPriceChange,
  onDiscountChange,
  onDiscountCommentChange = null,
  onDeleteItem,
  showVatAsAmount = false,        
  showVatBoth = true,
  enableVatTooltip = true,
  isEditMode = false,
  isQuickOrder = false,
  isLoading = false,
  getItemDetails = null,
  isItemPending = null,
  onValidationError = null,
  customerId = null,
  onAllotQtyChange = null,
  newlyAddedProductId = null,
  onQuantityPopupClose = null,
  onQuantityPopupOpen = null,
  onDiscountChangeComplete = null,
  onPriceChangeComplete = null,
  enableProductSplit = false,
  onPromoQtyLineOpenDrawer = null,
  onPromoQtyOpenPromotionsModal = null,
  bundleDiscounts = EMPTY_BUNDLE_DISCOUNTS,
  bundleLineDiscountByKey = null,
  isTelecaller = false,
  rhpCompactColumns = false,
  onCartRowIndexChange = null,
  selectedRowIndex = null,
}) => {
  const isRhp = localStorage.getItem('accessToken') === 'rhp' || localStorage.getItem('accessToken') === 'hp';
  const keepQtyFocusAfterAdd = isTelecaller || isRhp || PREFIX === "rhp";
  const hideTypeColumn = rhpCompactColumns;
  const hideBoxPriceColumn = rhpCompactColumns;
  const hideVatColumn = rhpCompactColumns;
  const nonEditColSpan =
    11 - (hideTypeColumn ? 1 : 0) - (hideBoxPriceColumn ? 1 : 0) - (hideVatColumn ? 1 : 0);
  const canSortCart = isTelecaller;
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = useCallback((columnId) => {
    if (!canSortCart) return;
    setSortBy((prev) => {
      if (prev === columnId) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return columnId;
    });
  }, [canSortCart]);

  const baseRows = useMemo(
    () =>
      applyShowPromoLabelDedupe(
        Array.isArray(cartItems) ? cartItems.filter(Boolean) : [],
        bundleDiscounts
      ),
    [cartItems, bundleDiscounts]
  );

  const keyboardRows = useMemo(() => {
    if (!canSortCart || !sortBy) return baseRows;
    const sorted = [...baseRows];
    sorted.sort((a, b) => {
      const aVal = sortBy === "code" ? getCartLineCode(a) : getCartLineDescription(a);
      const bVal = sortBy === "code" ? getCartLineCode(b) : getCartLineDescription(b);
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [baseRows, canSortCart, sortBy, sortDirection]);

  const cartRevision = useMemo(
    () => buildCartRevisionToken(keyboardRows),
    [keyboardRows]
  );
  const cartBucketsCacheRef = useRef({ revision: "", qty: null, promo: null });
  const { qtyControlMaxBuckets, promoGroupBuckets } = useMemo(() => {
    const cache = cartBucketsCacheRef.current;
    if (cache.revision === cartRevision && cache.qty && cache.promo) {
      return {
        qtyControlMaxBuckets: cache.qty,
        promoGroupBuckets: cache.promo,
      };
    }
    const qty = buildQtyControlMaxBuckets(keyboardRows);
    const promo = buildCartPromoGroupBuckets(keyboardRows);
    cartBucketsCacheRef.current = { revision: cartRevision, qty, promo };
    return { qtyControlMaxBuckets: qty, promoGroupBuckets: promo };
  }, [cartRevision, keyboardRows]);
  const cartRowVersions = useMemo(() => {
    const versions = new Array(keyboardRows.length);
    for (let i = 0; i < keyboardRows.length; i += 1) {
      const rowItem = keyboardRows[i];
      versions[i] = buildCartTableRowVersion(
        rowItem,
        getQtyControlBucketItems(rowItem, qtyControlMaxBuckets),
        getCartPromoGroupItemsFromBuckets(rowItem, promoGroupBuckets),
        bundleDiscounts,
        bundleLineDiscountByKey
      );
    }
    return versions;
  }, [
    keyboardRows,
    qtyControlMaxBuckets,
    promoGroupBuckets,
    bundleDiscounts,
    bundleLineDiscountByKey,
  ]);
  const itemDetailsByIndexRef = useRef([]);
  const itemDetailsByIndex = useMemo(() => {
    const prevList = itemDetailsByIndexRef.current;
    const nextList = keyboardRows.map((rowItem, i) => {
      const version = cartRowVersions[i];
      const id = rowItem?.id;
      const prev = prevList[i];
      if (prev && prev.version === version && prev.id === id) {
        return prev;
      }
      return {
        version,
        id,
        details: getItemDetails ? getItemDetails(rowItem) : null,
        fields: getCartItemNormalizedFields(rowItem),
      };
    });
    itemDetailsByIndexRef.current = nextList;
    return nextList;
  }, [keyboardRows, cartRowVersions, getItemDetails]);

  const rowCount = keyboardRows.length;
  const [keyboardRowIndex, setKeyboardRowIndex] = useCartTableKeyboardNavigation(rowCount);
  const tableContainerRef = useRef(null);
  const scrollTopRef = useRef(0);
  const viewportHeightRef = useRef(600);
  const scrollRafRef = useRef(0);
  const visibleRangeRef = useRef(EMPTY_VISIBLE_RANGE);
  const [visibleRange, setVisibleRange] = useState(EMPTY_VISIBLE_RANGE);
  const shouldVirtualize = isQuickOrder && !isEditMode && rowCount > VIRTUALIZE_AFTER_ROWS;
  const rowCountRef = useRef(rowCount);
  rowCountRef.current = rowCount;
  const shouldVirtualizeRef = useRef(shouldVirtualize);
  shouldVirtualizeRef.current = shouldVirtualize;

  const commitVisibleRange = useCallback((next) => {
    const prev = visibleRangeRef.current;
    if (
      prev.start === next.start &&
      prev.end === next.end &&
      prev.padTop === next.padTop &&
      prev.padBottom === next.padBottom
    ) {
      return;
    }
    visibleRangeRef.current = next;
    setVisibleRange(next);
  }, []);

  const scheduleVisibleRangeUpdate = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      const container = tableContainerRef.current;
      if (container) {
        scrollTopRef.current = container.scrollTop || 0;
        if (container.clientHeight) {
          viewportHeightRef.current = container.clientHeight;
        }
      }
      commitVisibleRange(
        computeCartVisibleRange(
          scrollTopRef.current,
          viewportHeightRef.current,
          rowCountRef.current,
          shouldVirtualizeRef.current
        )
      );
    });
  }, [commitVisibleRange]);

  useEffect(() => {
    commitVisibleRange(
      computeCartVisibleRange(
        scrollTopRef.current,
        viewportHeightRef.current,
        rowCount,
        shouldVirtualize
      )
    );
  }, [rowCount, shouldVirtualize, commitVisibleRange]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !shouldVirtualize) return undefined;
    scrollTopRef.current = container.scrollTop || 0;
    if (container.clientHeight) {
      viewportHeightRef.current = container.clientHeight;
    }
    scheduleVisibleRangeUpdate();
    const onScroll = () => {
      scrollTopRef.current = container.scrollTop || 0;
      scheduleVisibleRangeUpdate();
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (container.clientHeight) {
          viewportHeightRef.current = container.clientHeight;
        }
        scrollTopRef.current = container.scrollTop || 0;
        scheduleVisibleRangeUpdate();
      });
      ro.observe(container);
    }
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = 0;
      }
      ro?.disconnect?.();
    };
  }, [shouldVirtualize, scheduleVisibleRangeUpdate]);

  useEffect(() => {
    if (!newlyAddedProductId) return;
    const container = tableContainerRef.current;
    if (!container) return;
    const index = (() => {
      const pid = String(newlyAddedProductId);
      const buyIndex = keyboardRows.findIndex(
        (item) =>
          item &&
          String(item.product_id || item.id || "") === pid &&
          !isCartLineGetOrFreeRole(item)
      );
      if (buyIndex !== -1) return buyIndex;
      return keyboardRows.findIndex(
        (item) => item && String(item.product_id || item.id || "") === pid
      );
    })();
    if (index === -1) return;
    if (shouldVirtualize) {
      const top = Math.max(0, index * ROW_ESTIMATE_PX - ROW_ESTIMATE_PX * 2);
      container.scrollTop = top;
      scrollTopRef.current = top;
      scheduleVisibleRangeUpdate();
      return;
    }
    const rowEl = container.querySelector(`[data-cart-row="${index}"]`);
    if (rowEl && typeof rowEl.scrollIntoView === "function") {
      rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [newlyAddedProductId, keyboardRows, shouldVirtualize, scheduleVisibleRangeUpdate]);

  useEffect(() => {
    if (!rhpCompactColumns) {
      return;
    }
    if (selectedRowIndex === null) {
      setKeyboardRowIndex(null);
    }
  }, [selectedRowIndex, rhpCompactColumns, setKeyboardRowIndex]);

  useEffect(() => {
    const item =
      keyboardRowIndex !== null
        ? keyboardRows[keyboardRowIndex] ?? null
        : null;
    onCartRowIndexChange?.(keyboardRowIndex, item);
  }, [keyboardRowIndex, keyboardRows, onCartRowIndexChange]);

  useEffect(() => {
    if (keyboardRowIndex === null) return;
    if (shouldVirtualize) {
      const container = tableContainerRef.current;
      if (container) {
        const rowTop = keyboardRowIndex * ROW_ESTIMATE_PX;
        const rowBottom = rowTop + ROW_ESTIMATE_PX;
        if (rowTop < container.scrollTop) {
          container.scrollTop = rowTop;
          scrollTopRef.current = rowTop;
          scheduleVisibleRangeUpdate();
        } else if (rowBottom > container.scrollTop + container.clientHeight) {
          const nextTop = Math.max(0, rowBottom - container.clientHeight);
          container.scrollTop = nextTop;
          scrollTopRef.current = nextTop;
          scheduleVisibleRangeUpdate();
        }
      }
    }
    const active = document.activeElement;
    if (!(active instanceof Element)) return;
    if (isOfferProductsDialogOpen()) return;
    if (isInsideModalDialog(active)) return;
    const fromQty = active.closest("[data-cart-qty-scope]");
    const fromRow = active.closest("[data-cart-row]");
    let prevIdx = null;
    if (fromQty) {
      prevIdx = Number(fromQty.getAttribute("data-cart-qty-scope"));
    } else if (fromRow) {
      prevIdx = Number(fromRow.getAttribute("data-cart-row"));
    }
    if (!Number.isFinite(prevIdx) || prevIdx === keyboardRowIndex) return;
    const nextScope = document.querySelector(`[data-cart-qty-scope="${keyboardRowIndex}"]`);
    if (!nextScope) return;
    const first = nextScope.querySelector('button:not([disabled]), [tabindex="0"], [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
    requestAnimationFrame(() => first?.focus?.());
  }, [keyboardRowIndex, shouldVirtualize, scheduleVisibleRangeUpdate]);

  useEffect(() => {
    if (rowCount === 0) return;
    const onKeyDown = (e) => {
      if (e.key !== "Tab" || e.shiftKey) return;
      if (keyboardRowIndex === null) return;
      const active = document.activeElement;
      if (!(active instanceof Element)) return;
      if (isOfferProductsDialogOpen()) return;
    if (isInsideModalDialog(active)) return;
      const rowEls = document.querySelectorAll(`[data-cart-row="${keyboardRowIndex}"]`);
      const insideHighlightedRow = Array.from(rowEls).some((el) => el.contains(active));
      if (insideHighlightedRow) return;
      const qtyScope = document.querySelector(`[data-cart-qty-scope="${keyboardRowIndex}"]`);
      if (!qtyScope) return;
      e.preventDefault();
      const first = qtyScope.querySelector('button:not([disabled]), [tabindex="0"], [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
      first?.focus?.();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [rowCount, keyboardRowIndex]);

  const handleQtyRegionFocusChange = useCallback((index) => {
    if (typeof index === "number" && Number.isFinite(index)) {
      setKeyboardRowIndex(index);
    }
  }, [setKeyboardRowIndex]);

  const renderCartRow = useCallback((item, index) => {
      if (!item) return null;
      const itemMeta = itemDetailsByIndex[index];
      const itemDetails = itemMeta?.details ?? null;
      const itemNorm = itemMeta?.fields ?? null;
      const isPending = isItemPending ? isItemPending(item.product_id, item.id) : false;
      const productId = String(item.product_id || item.id || "");
      const isOfferLine = isCartLineGetOrFreeRole(item);
      const isInlineQtyTarget =
        !rhpCompactColumns &&
        keepQtyFocusAfterAdd &&
        isQuickOrder &&
        newlyAddedProductId &&
        String(newlyAddedProductId) === productId &&
        !isOfferLine;
      const isNewlyAdded =
        !rhpCompactColumns &&
        newlyAddedProductId &&
        String(newlyAddedProductId) === productId &&
        !isOfferLine;
      const shouldAutoOpen = Boolean(isNewlyAdded && !keepQtyFocusAfterAdd);
      const shouldAutoFocusQty = Boolean(isInlineQtyTarget);
      const orderType = item.order_type || item.type || "Box";
      let promoType = item.promo_type || item.promoType || item.promosType || null;
      if (!promoType && item.buyOrGet) {
        const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
        promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
      }
      const promoTypeUpper = String(promoType || "").toUpperCase();
      const isGetOrFreeLine = promoTypeUpper === "GET" || promoTypeUpper === "FREE";
      const promoTypeSuffix = promoType ? `_${promoType}` : "";
      const stableKey =
        isQuickOrder && item.product_id
          ? isGetOrFreeLine
            ? `${item.product_id}_${orderType}_${promoTypeUpper}_${item.id || index}`
            : `${item.product_id}_${orderType}_${item.id || index}`
          : item.product_id
            ? `${item.product_id}_${orderType}${promoTypeSuffix}_${item.id || index}`
            : `${item.id || index}_${orderType}`;
      const inlineEditKey = `${productId}_${orderType}`;
      const isKeyboardHighlighted =
        rhpCompactColumns && selectedRowIndex !== null && selectedRowIndex !== undefined
          ? selectedRowIndex === index
          : keyboardRowIndex !== null && keyboardRowIndex === index;

      return (
        <CartTableRow
          key={stableKey}
          item={item}
          isRhp={isRhp}
          index={index}
          isKeyboardHighlighted={isKeyboardHighlighted}
          onQtyRegionFocusChange={handleQtyRegionFocusChange}
          onTypeChange={onTypeChange}
          onAddAsNewType={onAddAsNewType}
          onQuantityChange={onQuantityChange}
          onPriceChange={onPriceChange}
          onDiscountChange={onDiscountChange}
          onDiscountCommentChange={onDiscountCommentChange}
          onDelete={onDeleteItem}
          showVatAsAmount={showVatAsAmount}
          showVatBoth={showVatBoth}
          enableVatTooltip={enableVatTooltip}
          isLoading={isPending}
          itemDetails={itemDetails}
          itemNorm={itemNorm}
          onValidationError={onValidationError}
          customerId={customerId}
          isEditMode={isEditMode}
          isQuickOrder={isQuickOrder}
          onAllotQtyChange={onAllotQtyChange}
          autoOpenQuantityPopup={shouldAutoOpen}
          autoFocusQuantityInput={shouldAutoFocusQty && !isGetOrFreeLine}
          quantityInlineEditKey={inlineEditKey}
          onQuantityPopupClose={onQuantityPopupClose}
          onQuantityPopupOpen={onQuantityPopupOpen}
          onDiscountChangeComplete={onDiscountChangeComplete}
          onPriceChangeComplete={onPriceChangeComplete}
          enableProductSplit={enableProductSplit}
          onPromoQtyLineOpenDrawer={onPromoQtyLineOpenDrawer}
          onPromoQtyOpenPromotionsModal={onPromoQtyOpenPromotionsModal}
          rowVersion={cartRowVersions[index]}
          qtyControlBucketItems={getQtyControlBucketItems(item, qtyControlMaxBuckets)}
          promoGroupItems={getCartPromoGroupItemsFromBuckets(item, promoGroupBuckets)}
          bundleDiscounts={bundleDiscounts}
          bundleLineDiscountByKey={bundleLineDiscountByKey}
          isTelecaller={isTelecaller}
          hideTypeColumn={hideTypeColumn}
          hideBoxPriceColumn={hideBoxPriceColumn}
          hideVatColumn={hideVatColumn}
          rhpReadOnlyPricing={rhpCompactColumns}
        />
      );
  }, [
    getItemDetails,
    itemDetailsByIndex,
    isItemPending,
    rhpCompactColumns,
    keepQtyFocusAfterAdd,
    isQuickOrder,
    newlyAddedProductId,
    selectedRowIndex,
    keyboardRowIndex,
    isRhp,
    handleQtyRegionFocusChange,
    onTypeChange,
    onAddAsNewType,
    onQuantityChange,
    onPriceChange,
    onDiscountChange,
    onDiscountCommentChange,
    onDeleteItem,
    showVatAsAmount,
    showVatBoth,
    enableVatTooltip,
    onValidationError,
    customerId,
    isEditMode,
    onAllotQtyChange,
    onQuantityPopupClose,
    onQuantityPopupOpen,
    onDiscountChangeComplete,
    onPriceChangeComplete,
    enableProductSplit,
    onPromoQtyLineOpenDrawer,
    onPromoQtyOpenPromotionsModal,
    cartRowVersions,
    qtyControlMaxBuckets,
    promoGroupBuckets,
    bundleDiscounts,
    bundleLineDiscountByKey,
    isTelecaller,
    hideTypeColumn,
    hideBoxPriceColumn,
    hideVatColumn,
  ]);

  const displayRange = useMemo(() => {
    if (!shouldVirtualize) {
      return { start: 0, end: rowCount, padTop: 0, padBottom: 0 };
    }
    const end = Math.max(0, Math.min(visibleRange.end, rowCount));
    const start = Math.max(0, Math.min(visibleRange.start, end));
    return {
      start,
      end,
      padTop: start * ROW_ESTIMATE_PX,
      padBottom: Math.max(0, (rowCount - end) * ROW_ESTIMATE_PX),
    };
  }, [shouldVirtualize, visibleRange, rowCount]);

  const renderedRows = useMemo(() => {
    const { start, end, padTop, padBottom } = displayRange;
    const rows = [];
    if (padTop > 0) {
      rows.push(
        <TableRow key="cart-virtual-pad-top" aria-hidden sx={{ height: padTop }}>
          <TableCell
            colSpan={isEditMode ? 13 : nonEditColSpan}
            sx={{ p: 0, border: "none", height: padTop, lineHeight: 0 }}
          />
        </TableRow>
      );
    }
    for (let index = start; index < end; index += 1) {
      const rowItem = keyboardRows[index];
      if (!rowItem) continue;
      rows.push(renderCartRow(rowItem, index));
    }
    if (padBottom > 0) {
      rows.push(
        <TableRow key="cart-virtual-pad-bottom" aria-hidden sx={{ height: padBottom }}>
          <TableCell
            colSpan={isEditMode ? 13 : nonEditColSpan}
            sx={{ p: 0, border: "none", height: padBottom, lineHeight: 0 }}
          />
        </TableRow>
      );
    }
    return rows;
  }, [displayRange, keyboardRows, renderCartRow, isEditMode, nonEditColSpan]);

  return (
    <TableContainer
      ref={tableContainerRef}
      component={Box}
      sx={{
        mt: isEditMode ? 1 : 0, 
        ...(isQuickOrder
          ? {
              mx: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }
          : {
              mx: { xs: -2, sm: -2, md: -2 },
              width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 32px)', md: 'calc(100% + 32px)' },
            }),
        borderRadius: 0, 
        height: isEditMode ? 'calc(100vh - 275px)' : isQuickOrder ? 'calc(100vh - 200px)' : 'calc(100vh - 266px)', 
        overflowX: 'auto',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          height: '8px',
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px'
        },
        '& .MuiTableBody-root > .MuiTableRow-root': {
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 56px',
        },
      }}
    >
      <Table
        aria-label="epos table"
        size="small"
        className="table-cell-border"
        sx={{
          tableLayout: 'auto',
          minWidth: isEditMode ? 1200 : 900,
        }}
      >
        <TableHead>
          <TableRow className="table-cell-header">
            {isEditMode ? (
              <>
                <TableCell sx={{ minWidth: 40, textAlign: 'center' }}>No</TableCell>
                <TableCell sx={{ minWidth: 50, padding: "4px" }}>Code</TableCell>
                <TableCell sx={{ minWidth: 50, padding: "4px" }}>Type</TableCell>
                <TableCell sx={{ minWidth: 300, padding: "4px" }}>Product Description</TableCell>
                <TableCell sx={{ minWidth: 100, textAlign: 'right', padding: "4px" }}>Original Price</TableCell>
                <TableCell sx={{ minWidth: 80, textAlign: 'center', padding: "4px" }}>Order Qty</TableCell>
                <TableCell sx={{ minWidth: 90, textAlign: 'center', padding: "4px" }}>Allot Qty</TableCell>
                <TableCell sx={{ minWidth: 70, textAlign: 'right', padding: "4px" }}>Unit Price</TableCell>
                <TableCell sx={{ minWidth: 100, textAlign: 'center', padding: "4px" }}>Item Disc</TableCell>
                <TableCell sx={{ minWidth: 60, textAlign: 'right', padding: "4px" }}>Total</TableCell>
                <TableCell sx={{ minWidth: 60, textAlign: 'center', padding: "4px" }}>VAT %</TableCell>
                <TableCell sx={{ minWidth: 40, textAlign: 'center', padding: "4px" }}></TableCell>
              </>
            )  : (
              <>
                <TableCell sx={{ width: 50, textAlign: 'center', padding: "4px" }}>No</TableCell>
                <TableCell sx={{ width: 100, padding: "4px" }}>
                  {canSortCart ? (
                    <TableSortLabel
                      active={sortBy === "code"}
                      direction={sortBy === "code" ? sortDirection : "asc"}
                      onClick={() => handleSort("code")}
                      hideSortIcon={false}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          opacity: "1 !important",
                          visibility: "visible !important",
                        },
                      }}
                    >
                      Code
                    </TableSortLabel>
                  ) : (
                    "Code"
                  )}
                </TableCell>
                <TableCell sx={{ width: !isRhp ? 250 : 300, padding: "4px" }}>
                  {canSortCart ? (
                    <TableSortLabel
                      active={sortBy === "description"}
                      direction={sortBy === "description" ? sortDirection : "asc"}
                      onClick={() => handleSort("description")}
                      hideSortIcon={false}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          opacity: "1 !important",
                          visibility: "visible !important",
                        },
                      }}
                    >
                      Product Description
                    </TableSortLabel>
                  ) : (
                    "Product Description"
                  )}
                </TableCell>
                {!hideTypeColumn ? (
                  <TableCell sx={{ width: 50,padding: "4px" }}>Type</TableCell>
                ) : null}
                <TableCell sx={{ width: 100, textAlign: 'center', padding: "4px" }}>QTY</TableCell>
                {!hideBoxPriceColumn ? (
                  <TableCell sx={{ width: 90, textAlign: 'right', padding: "4px" }}>{isQuickOrder ? 'Std. Price' : 'Box Price'}</TableCell>
                ) : null}
                <TableCell sx={{ width: 90, textAlign: 'right', padding: "4px" }}>{rhpCompactColumns ? 'Price' : (isQuickOrder ? 'Offer Price' : 'Unit Price')}</TableCell>
                <TableCell sx={{ width: 120, minWidth: 120, textAlign: rhpCompactColumns ? 'right' : 'center', padding: "4px" }}>{rhpCompactColumns ? 'Discount' : 'Disc'}</TableCell>
                <TableCell sx={{ width: 70, textAlign: 'right', padding: "4px" }}>Total</TableCell>
                {!hideVatColumn ? (
                  <TableCell sx={{ width: 60, textAlign: 'right', padding: "4px" }}>VAT</TableCell>
                ) : null}
                <TableCell sx={{ width: 40, textAlign: 'center', padding: "4px" }}></TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && cartItems.length === 0 ? (
            Array.from({ length: 3 }).map((_, index) => (
              <CartTableRowSkeleton key={index} />
            ))
          ) : cartItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isEditMode ? 13 : nonEditColSpan} align="center" sx={{ py: 3, border: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Iconify 
                    icon="solar:cart-large-bold" 
                    width={80} 
                    sx={{ color: 'text.disabled', mb: 2 }} 
                  />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    No products added yet
                  </Typography>
                  <Typography variant="body2" color="#0e0f0fef">
                    {isEditMode ? "Use Add New Product to add items" : "Use the product search above to add items"}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            renderedRows
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(CartTable);

